import Media from 'react-media';
import type { BreakpointsTypes } from '@etg/wings';
import {
  breakpoints,
  Button,
  Dialog,
  Heading,
  Inline,
  LinkButton,
  Notice,
  Stack,
  Text,
  vars,
} from '@etg/wings';

import { Notification, variants } from '@eti/components';
import { useDirection, useTranslation } from '@eti/providers';
import { css, etiColors, mediaQueries } from '@eti/styles';
import { Airplane, Share } from '@phosphor-icons/react';
import { AnimatePresence, m as motion } from 'framer-motion';
import type { TripType } from '@eti/schema-types';
import { useToggle } from '../../common/custom-hooks/toggle';
import useClipboard from '../../common/custom-hooks/useClipboard';
import type { BoundsType } from '../../common/types/transformations/models';
import ShareTripFlightDetails from './ShareTripFlightDetails';

interface ShareTripProps {
  bounds: BoundsType;
  isSponsoredTrip?: boolean;
  priceDisplayText: string;
  shareableUrl: string;
  totalPrice?: string;
  type: TripType;
  breakpoint?: BreakpointsTypes;
}

const buttonLabelStyle = css`
  font-size: 0.875rem;
  height: 40px;
  padding: 0 18.5px;

  @media ${mediaQueries.medium.up} {
    height: 40px;
  }
`;
const inputWrapperStyles = css`
  position: relative;
`;

const linkBoxStyles = css`
  align-items: center;
  background-color: #f8f8f8;
  border: 1px solid #bbb;
  border-radius: 3px;
  color: #666;
  cursor: not-allowed;
  display: flex;
  font-size: 1rem;
  padding: 5px 3px 5px 10px;
  position: relative;
`;

const metaLinkStyles = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre;
  width: 100%;
`;

const noticeStyles = css`
  position: absolute;
  right: 0;
`;

const priceStyles = (isSponsoredTrip?: boolean) => css`
  color: ${etiColors.black};
  display: flex;
  flex: 0;
  justify-content: right;
  max-height: 24px;

  @media ${mediaQueries.medium.up} {
    ${!isSponsoredTrip && 'flex: 1; flex-end; width: 100%;'}
  }
`;

const priceWrapperStyles = css`
  margin-left: auto;
`;

const sponsoredTripStyles = css`
  color: ${etiColors.black};
  flex: 0 0 100%;
  max-height: 20px;

  @media ${mediaQueries.medium.up} {
    flex: 1;
    width: 100%;
  }
`;

const tripInfoStyles = css`
  border: 1px solid ${vars.colors.divider};
  border-radius: 3px;
  padding: 16px;
  width: 100%;
`;

const ShareTrip = ({
  bounds,
  isSponsoredTrip,
  priceDisplayText,
  shareableUrl,
  totalPrice,
  type,
  breakpoint,
}: ShareTripProps) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const [isCopied, copyToClipboard] = useClipboard();
  const { isOpen, handleOpen, handleClose } = useToggle(false);
  const shareTripButtonId = isSponsoredTrip ? '-sponsored' : '';

  const shareUrl = async () => {
    if (navigator.share) {
      await navigator.share({ url: shareableUrl });
    } else {
      handleOpen();
    }
  };

  return (
    <>
      <LinkButton data-testid={`shareTrip-button${shareTripButtonId}`} onClick={shareUrl}>
        <Media
          query={`(min-width: ${breakpoint || breakpoints._560})`}
          render={() => (
            <Inline spacing={4}>
              <Share data-testid="shareTrip-icon" mirrored={isRTL} size={20} weight="light" />
              {t('BookingFlow.ShareTrip.Label')}
            </Inline>
          )}
        />
      </LinkButton>
      <Dialog
        closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
        data-testid="shareTrip-modal"
        isOpen={isOpen}
        onDismiss={handleClose}
        title={t('BookingFlow.ShareTrip.Modal.Header.Text')}
      >
        <Stack spacing={24}>
          <Inline alignY="center" className={tripInfoStyles}>
            <Airplane
              color="#262626"
              data-testid="shareTrip-modal-trip-summary-icon"
              size={20}
              weight="light"
            />
            <ShareTripFlightDetails bounds={bounds} type={type} />
            <div className={priceWrapperStyles}>
              <div
                className={priceStyles(isSponsoredTrip)}
                data-testid="result-trip-price-standard"
              >
                {totalPrice}
              </div>
              {!isSponsoredTrip && (
                <div className={sponsoredTripStyles}>
                  <Text>{priceDisplayText}</Text>
                </div>
              )}
            </div>
          </Inline>
          <Stack>
            <Heading level={3}>{t('BookingFlow.ShareTrip.Modal.CopyLink.Label')}</Heading>
            <div className={inputWrapperStyles}>
              <AnimatePresence>
                {isCopied && (
                  <motion.div
                    animate="visible"
                    className={noticeStyles}
                    exit="hidden"
                    initial="hidden"
                    variants={{
                      visible: { y: -24 },
                      hidden: { y: 16 },
                    }}
                  >
                    <Notice>{t('BookingFlow.ShareTrip.Modal.Tooltip.Text')}</Notice>
                  </motion.div>
                )}
              </AnimatePresence>
              <div className={linkBoxStyles}>
                <div className={metaLinkStyles} data-testid="shareTrip-link">
                  {shareableUrl}
                </div>
                <Button
                  className={buttonLabelStyle}
                  data-testid="shareTrip-modal-copy-button"
                  onClick={() => copyToClipboard(shareableUrl)}
                  variant={variants.SECONDARY}
                >
                  {t('BookingFlow.ShareTrip.Modal.CopyLinkButton.Text')}
                </Button>
              </div>
            </div>
            <Notification dataTestId="shareTrip-modal-notification">
              {t('BookingFlow.ShareTrip.Modal.Notification.Text')}
            </Notification>
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
};

export default ShareTrip;
